<style scoped lang="scss">
  .payment-methods {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 15px;

    @include media(max, lg) {
      grid-template-columns: repeat(3, 1fr);
    }

    @include media(max, sm) {
      grid-template-columns: repeat(2, 1fr);
    }

    .methods-additional-button, .payment-method {
      height: 110px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border: 1px solid $line-color;
      border-radius: 10px;

      background-color: white;
      cursor: pointer;
      user-select: none;
      transition: $control-transition;

      &:hover {
        border-color: $line-hover-color;
      }
    }

    .methods-additional-button {
      font-size: 11px;
      font-weight: 500;
      text-align: center;
      @include padding-x(30px);
    }

    .payment-method {
      &.active {
        border-color: var(--theme-color);
        background-color: rgba(var(--theme-color-rgb), .05);
      }

      .method-icon {
        .icon {
          max-width: 120px;
          max-height: 32px;
          height: 32px;
          width: 100%;
        }
      }

      .method-description {
        margin-top: 10px;
        font-size: 11px;
        text-align: center;
        @include padding-x(12px);
      }
    }
  }
</style>

<template>
  <div class="payment-methods">
    <div
      v-for="method in paymentMethods"
      :key="method.code"
      class="payment-method"
      :class="{active: paymentMethod === method.code}"
      @click="selectPaymentMethod(method.code)">
      <div class="method-icon">
        <icon :name="method.icon" :inline="false"/>
      </div>
      <div v-if="method.title" class="method-description">
        {{ method.title }}
      </div>
    </div>
    <div
      v-if="!allMethodsVisible"
      class="methods-additional-button"
      @click="showAllMethods">
      {{ $t('payment.otherPaymentMethods') }}
    </div>
  </div>
</template>

<script>

const paymentMethodsIcons = {
  bankCardEur: 'payment-cards',
  bankCardRub: 'payment-cards-rub',
  bankCardRubBank131: 'payment-cards-rub',
  bankCardRubSmgl: 'payment-cards-rub',
  bankCardUsd: 'payment-cards',
  bankCardYuno: 'payment-cards',
  bankCardYunoEur: 'payment-cards',
  bankCardYunoKzt: 'payment-cards',
  bankCardYunoUzs: 'payment-cards',
  applePay: 'payment-apple-pay',
  googlePay: 'payment-google-pay',
  boaCompraBankUsd: 'payment-br-bank',
  boaCompraMasterCardUsd: 'payment-mastercard',
  boaCompraCardsUsd: 'payment-br-card',
  boaCompraVisaUsd: 'payment-visa',
  fakeMobile: 'payment-sms',
  adyenSofortEur: 'payment-klarna',
  cryptocurrencies: 'payment-cryptocurrencies',
  sberPay: 'payment-sberpay',
  sberPaySmgl: 'payment-sberpay',
  sberMyCom: 'payment-sberbank',
  vkPay: 'payment-vkpay',
  payPalEur: 'payment-paypal',
  payPalRub: 'payment-paypal',
  payPalUsd: 'payment-paypal',
  payPalYunoUsd: 'payment-paypal',
  paySaferCardEur: 'payment-paysafe',
  paySaferCardUsd: 'payment-paysafe',
  qiwiMyCom: 'payment-qiwi',
  webMoneyUsd: 'payment-webmoney',
  webMoneyEur: 'payment-webmoney',
  yandexMoneyMyCom: 'payment-y-money',
  fasterPaymentsSystem: 'payment-faster-payments-system',
  fasterPaymentsSystemBank131: 'payment-faster-payments-system',
  fasterPaymentsSystemSmgl: 'payment-faster-payments-system',
  boletoFlashBrl: 'payment-boleto-flash',
  pix: 'payment-pix',
  trustly: 'payment-trustly',
}

export default {
  name: 'PaymentMethods',
  data() {
    return {
      allMethodsVisible: true,
    }
  },
  computed: {
    ...mapGetters('currencies', ['getAmountWithCurrency']),
    ...mapState('payment', ['paymentAmount']),
    ...mapFields('payment', ['paymentMethod']),
    ...mapGetters('payment', ['getPaymentMethod', 'paymentMethodsList', 'selectedPaymentMethod']),

    paymentMethodsFiltered() {
      if (!this.allMethodsVisible && this.paymentMethodsList.length) {
        const additionalMethodsListSize = {
          sm: 1,
          md: 2,
          lg: 2,
          xl: 3,
        }

        return [
          this.selectedPaymentMethod,
          ...this.paymentMethodsList.filter(method => method.code !== this.paymentMethod),
        ].slice(0, additionalMethodsListSize[this.$appSize])
      }

      return this.paymentMethodsList
    },

    paymentMethods() {
      return this.paymentMethodsFiltered.map(method => ({
        ...method,
        icon: paymentMethodsIcons[method.code],
      }))
    },
  },
  methods: {
    selectPaymentMethod(method) {
      this.paymentMethod = method

      this.allMethodsVisible = false

      this.$ga.action({
        category: 'DonationPage-PaymentMethods',
        action: `btn_payment_${method}`,
      })

      this.$tmr.goal('paymentSystem_changed', {
        paymentSystem: method,
      })
    },

    showAllMethods() {
      this.allMethodsVisible = true

      this.$ga.action({
        category: 'DonationPage-PaymentMethods',
        action: 'btn_more',
      })

      this.$tmr.goal('paymentSystem_more')
    },
  },
  watch: {
    paymentMethod: {
      handler(value) {
        this.allMethodsVisible = !value
      },
      immediate: true,
    },
  },
}
</script>
